import React, {useCallback, useRef} from 'react';

import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigAutosaverPausedConditionType,
  FlowchartConfigMetadata,
} from '../redux/reduceFlowchartConfig';

export interface LiveStateChanges {
  dirtyState: [boolean, (isDirty: boolean) => void];
  readonlyState: [boolean, (isReadonly: boolean) => void];
  isSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
  isAutosaverPausedState: [
    boolean,
    (condition: FlowchartConfigAutosaverPausedConditionType, conditionValue: boolean) => void,
  ];
  isAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
}

export interface FlowchartConfigStateManagerChildrenProps {
  persistedFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata;
  draftFlowchartConfigAndMetadataRef: React.MutableRefObject<FlowchartConfigWithMetadata>;
  setFlowchartConfigAndMetadata: (params: {
    flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata | null;
    skipSettingDirtyState?: boolean;
  }) => void;
  setFlowchartConfigMetadata: (metadata: FlowchartConfigMetadata, skipSettingDirtyState?: boolean) => void;
  liveStateChanges: LiveStateChanges;
  lastSaveError: Error | null;
}
export interface BaseFlowchartConfigStateManagerProps {
  flowchartConfigData: FlowchartConfigWithMetadata;
  setFlowchartConfigData: (flowchartConfigData: FlowchartConfigWithPartialMetadata) => void;
  liveStateChanges: LiveStateChanges;
  lastSaveError: Error | null;
  children: (props: FlowchartConfigStateManagerChildrenProps) => JSX.Element;
}

export type FlowchartConfigWithPartialMetadata =
  // Caller can provide one or both of `staticFlowchart` or `flowchartConfigMetadata`
  | {
      staticFlowchart: StaticTemplateFlowchart;
      flowchartConfigMetadata?: Partial<FlowchartConfigMetadata>;
    }
  | {
      staticFlowchart?: StaticTemplateFlowchart;
      flowchartConfigMetadata: Partial<FlowchartConfigMetadata>;
    };

/**
 * BaseFlowchartConfigStateManager: manages the draft flowchart config as the user makes edits.
 * This component receives flowchart data through props and de-couples the controller from the state management
 * system (Redux, useState, etc.). To use, implement a wrapper component like FlowchartConfigReduxStateManagerWrapper.
 */
export function BaseFlowchartConfigStateManager(props: BaseFlowchartConfigStateManagerProps): JSX.Element {
  const {setFlowchartConfigData, flowchartConfigData, liveStateChanges} = props;
  // The setDraftFlowchartConfig callback (below) will update the flowchart config, where changes will force a
  // re-render. However, we also keep a pointer to the latest flowchart config in a ref. The ref is used when sending data
  // to the server. This protects us from stale closures, as our saves happen in async functions, where state is
  // likely to be stale.
  const draftFlowchartConfigAndMetadataRef = useRef<FlowchartConfigWithMetadata>({
    ...flowchartConfigData,
  });

  const [, setIsDirty] = liveStateChanges.dirtyState;

  const isInitialized =
    flowchartConfigData?.staticFlowchart != null &&
    flowchartConfigData?.staticFlowchart.nodesStaticData.length > 0;

  // Initialize ref when the flowchart initializes
  if (
    isInitialized &&
    draftFlowchartConfigAndMetadataRef.current.staticFlowchart != null &&
    draftFlowchartConfigAndMetadataRef.current.staticFlowchart.nodesStaticData.length === 0
  ) {
    draftFlowchartConfigAndMetadataRef.current = flowchartConfigData;
  }

  const setFlowchartConfigAndMetadata = useCallback(
    ({
      flowchartConfigWithMetadata,
      skipSettingDirtyState = false,
    }: {
      flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata | null;
      skipSettingDirtyState?: boolean;
    }): void => {
      // Handle null case - this happens when applyFlowchartChangesCore returns null (no changes)
      if (!flowchartConfigWithMetadata) {
        return;
      }

      const {staticFlowchart, flowchartConfigMetadata} = flowchartConfigWithMetadata;
      // Track if any non-interactive data has changed that should trigger a dirty state and therefore a save
      let changedSomeNonInteractiveData = false;

      if (staticFlowchart != null) {
        draftFlowchartConfigAndMetadataRef.current.staticFlowchart = staticFlowchart;
        changedSomeNonInteractiveData = true;
      }
      if (flowchartConfigMetadata != null) {
        const definedMetadataEntries = Object.entries(flowchartConfigMetadata).filter(
          ([, value]) => value !== undefined
        );

        // Merge the partial metadata, only updating non-undefined fields
        draftFlowchartConfigAndMetadataRef.current.metadata = {
          ...draftFlowchartConfigAndMetadataRef.current.metadata,
          ...Object.fromEntries(definedMetadataEntries),
        };

        const hasNonInteractiveStateChanges = definedMetadataEntries.some(
          ([key]) => key !== 'interactionState'
        );
        if (hasNonInteractiveStateChanges) {
          changedSomeNonInteractiveData = true;
        }
      }

      if (!skipSettingDirtyState && changedSomeNonInteractiveData) {
        setIsDirty(true);
      }
      setFlowchartConfigData(flowchartConfigWithMetadata);
    },
    [setIsDirty, setFlowchartConfigData]
  );

  const setFlowchartConfigMetadata = useCallback(
    (metadata: FlowchartConfigMetadata, skipSettingDirtyState: boolean = false): void => {
      setFlowchartConfigAndMetadata({
        flowchartConfigWithMetadata: {flowchartConfigMetadata: metadata},
        skipSettingDirtyState,
      });
    },
    [setFlowchartConfigAndMetadata]
  );

  // Render loading until flowchart data has been initialized to prevent validation from running with incomplete data.
  // We assume that all flowcharts will have at least a start node that can't be deleted.
  if (!isInitialized) {
    return <CenteredHorizontalThrobber />;
  }

  return props.children({
    persistedFlowchartConfig: flowchartConfigData.staticFlowchart,
    flowchartConfigMetadata: flowchartConfigData.metadata,
    draftFlowchartConfigAndMetadata: flowchartConfigData,
    draftFlowchartConfigAndMetadataRef,
    setFlowchartConfigAndMetadata,
    setFlowchartConfigMetadata,
    liveStateChanges,
    lastSaveError: props.lastSaveError,
  });
}
